import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Bell, Lock, Key, Trash2, Plus, Copy, Check } from 'lucide-react';
import { get, post, patch, del } from '@/shared/api/api_client';
import { useAuth } from '@/features/auth';
import { Navbar } from '@/widgets/Navbar';
import { Button, Card, Input, Modal } from '@/shared/ui';
import { formatDate } from '@/shared/utils/format';
import type { User as AppUserType } from '@/entities/user';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface MCPToken {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
  prefix: string;
}

interface MCPTokensResponse {
  tokens: MCPToken[];
}

interface CreatedTokenResponse {
  token: string;
  id: string;
}

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------
const displayNameSchema = z.object({
  displayName: z.string().min(2, 'At least 2 characters').max(50),
});

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z.string().min(6, 'At least 6 characters'),
    confirmPassword: z.string().min(1, 'Required'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type DisplayNameValues = z.infer<typeof displayNameSchema>;
type PasswordValues = z.infer<typeof passwordSchema>;

// ---------------------------------------------------------------------------
// Toggle switch component
// ---------------------------------------------------------------------------
function Toggle({
  checked,
  onChange,
  label,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  id: string;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-4">
      <label htmlFor={id} className="text-sm text-slate-700 cursor-pointer">
        {label}
      </label>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 ${
          checked ? 'bg-primary-600' : 'bg-slate-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
        <span className="sr-only">{label}</span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revealed token display
// ---------------------------------------------------------------------------
function RevealedToken({ token }: { token: string }): React.ReactElement {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-4 space-y-3">
      <p className="text-xs font-semibold text-secondary-700">
        Save this token now — it won't be shown again!
      </p>
      <div className="flex gap-2">
        <div className="flex-1 break-all rounded border border-secondary-200 bg-white px-3 py-2 text-xs font-mono text-slate-800">
          {token}
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-secondary-200 bg-white text-secondary-700 hover:bg-secondary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 transition-colors duration-150"
          aria-label="Copy token"
        >
          {copied ? <Check size={15} /> : <Copy size={15} />}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AccountSettingsPage
// ---------------------------------------------------------------------------
export default function AccountSettingsPage(): React.ReactElement {
  const { appUser, setAppUser } = useAuth();
  const queryClient = useQueryClient();

  const role = appUser?.role ?? 'child';
  const familyId = appUser?.familyId ?? '';

  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [createTokenLoading, setCreateTokenLoading] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({
    choreApprovalEmail: appUser?.notificationPrefs?.choreApprovalEmail ?? true,
    redemptionEmail: appUser?.notificationPrefs?.redemptionEmail ?? true,
  });

  // Display name form
  const {
    register: registerName,
    handleSubmit: handleNameSubmit,
    formState: { errors: nameErrors, isSubmitting: nameSubmitting },
    setError: setNameError,
  } = useForm<DisplayNameValues>({
    resolver: zodResolver(displayNameSchema),
    defaultValues: { displayName: appUser?.displayName ?? '' },
  });

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors, isSubmitting: passwordSubmitting },
    setError: setPasswordError,
    reset: resetPassword,
  } = useForm<PasswordValues>({ resolver: zodResolver(passwordSchema) });

  // MCP tokens query (parent only)
  const { data: tokensData, isLoading: tokensLoading } = useQuery<MCPTokensResponse>({
    queryKey: ['mcp-tokens', familyId],
    queryFn: () => get<MCPTokensResponse>(`/families/${familyId}/mcp-tokens`),
    enabled: Boolean(familyId && role === 'parent'),
  });

  // Mutations
  const updateDisplayName = async (data: DisplayNameValues) => {
    try {
      const updated = await patch<AppUserType>('/users/me', { displayName: data.displayName });
      setAppUser(updated);
    } catch (err: unknown) {
      setNameError('root', { message: err instanceof Error ? err.message : 'Failed to update name.' });
    }
  };

  const updatePassword = async (data: PasswordValues) => {
    try {
      await post<void>('/users/me/change-password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      resetPassword();
      setShowPasswordSection(false);
    } catch (err: unknown) {
      setPasswordError('root', { message: err instanceof Error ? err.message : 'Failed to change password.' });
    }
  };

  const updateNotifPrefs = async (key: keyof typeof notifPrefs, value: boolean) => {
    const updated = { ...notifPrefs, [key]: value };
    setNotifPrefs(updated);
    try {
      await patch<void>('/users/me', { notificationPrefs: updated });
    } catch {
      // Revert on error
      setNotifPrefs(notifPrefs);
    }
  };

  const deleteTokenMutation = useMutation({
    mutationFn: (tokenId: string) => del<void>(`/families/${familyId}/mcp-tokens/${tokenId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['mcp-tokens', familyId] }),
  });

  const handleCreateToken = async () => {
    if (!newTokenName.trim()) return;
    setCreateTokenLoading(true);
    setCreatedToken(null);
    try {
      const res = await post<CreatedTokenResponse>(`/families/${familyId}/mcp-tokens`, {
        name: newTokenName.trim(),
      });
      setCreatedToken(res.token);
      setNewTokenName('');
      void queryClient.invalidateQueries({ queryKey: ['mcp-tokens', familyId] });
    } finally {
      setCreateTokenLoading(false);
    }
  };

  const handleRequestDeletion = async () => {
    try {
      await post<void>('/users/me/request-deletion');
      setShowDeleteConfirm(false);
      // Optionally show success message
    } catch {
      // Handle error
    }
  };

  const tokens = tokensData?.tokens ?? [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6 space-y-6">
        <div className="flex items-center gap-2">
          <User size={22} className="text-primary-600" aria-hidden="true" />
          <h1 className="text-xl font-bold text-slate-900">Account Settings</h1>
        </div>

        {/* 1. Display name */}
        <Card>
          <h2 className="mb-4 text-base font-semibold text-slate-800">Display Name</h2>
          <form onSubmit={handleNameSubmit(updateDisplayName)} noValidate className="flex gap-3">
            <Input
              placeholder="Your display name"
              error={nameErrors.displayName?.message}
              {...registerName('displayName')}
            />
            <Button type="submit" isLoading={nameSubmitting}>Save</Button>
          </form>
          {nameErrors.root && (
            <p role="alert" className="mt-2 text-xs text-danger-600">{nameErrors.root.message}</p>
          )}
        </Card>

        {/* 2. Email notifications (parent only) */}
        {role === 'parent' && (
          <Card>
            <h2 className="mb-4 text-base font-semibold text-slate-800 flex items-center gap-2">
              <Bell size={16} aria-hidden="true" />
              Email Notifications
            </h2>
            <div className="space-y-4">
              <Toggle
                id="toggle-chore-approval"
                label="Notify me when a chore is submitted for approval"
                checked={notifPrefs.choreApprovalEmail}
                onChange={(v) => void updateNotifPrefs('choreApprovalEmail', v)}
              />
              <Toggle
                id="toggle-redemption"
                label="Notify me when a reward is requested"
                checked={notifPrefs.redemptionEmail}
                onChange={(v) => void updateNotifPrefs('redemptionEmail', v)}
              />
            </div>
          </Card>
        )}

        {/* 3. Change password */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Lock size={16} aria-hidden="true" />
              Password
            </h2>
            <button
              type="button"
              onClick={() => setShowPasswordSection((v) => !v)}
              className="text-sm text-primary-600 hover:text-primary-800 font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 rounded"
            >
              {showPasswordSection ? 'Cancel' : 'Change password'}
            </button>
          </div>

          {showPasswordSection && (
            <form onSubmit={handlePasswordSubmit(updatePassword)} noValidate className="flex flex-col gap-4">
              <Input
                label="Current password"
                type="password"
                error={passwordErrors.currentPassword?.message}
                {...registerPassword('currentPassword')}
              />
              <Input
                label="New password"
                type="password"
                error={passwordErrors.newPassword?.message}
                {...registerPassword('newPassword')}
              />
              <Input
                label="Confirm new password"
                type="password"
                error={passwordErrors.confirmPassword?.message}
                {...registerPassword('confirmPassword')}
              />
              {passwordErrors.root && (
                <p role="alert" className="text-xs text-danger-600">{passwordErrors.root.message}</p>
              )}
              <Button type="submit" isLoading={passwordSubmitting}>Update Password</Button>
            </form>
          )}
        </Card>

        {/* 4. MCP Tokens (parent only) */}
        {role === 'parent' && (
          <Card>
            <h2 className="mb-1 text-base font-semibold text-slate-800 flex items-center gap-2">
              <Key size={16} aria-hidden="true" />
              MCP API Tokens
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Create tokens to allow AI assistants to manage KidKred on your behalf.
            </p>

            {/* Create new token */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="Token name (e.g. Claude Desktop)"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                aria-label="New token name"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleCreateToken(); } }}
              />
              <Button
                size="sm"
                isLoading={createTokenLoading}
                disabled={!newTokenName.trim()}
                onClick={() => void handleCreateToken()}
                leftIcon={<Plus size={14} />}
              >
                Create
              </Button>
            </div>

            {createdToken && <RevealedToken token={createdToken} />}

            {/* Existing tokens */}
            {tokensLoading ? (
              <div className="space-y-2 mt-4">
                {[1, 2].map((i) => <div key={i} className="animate-pulse h-12 rounded-lg bg-slate-50" />)}
              </div>
            ) : tokens.length === 0 ? (
              <p className="text-sm text-slate-400 mt-4">No tokens yet.</p>
            ) : (
              <div className="mt-4 space-y-2" role="list" aria-label="MCP tokens">
                {tokens.map((token) => (
                  <div
                    key={token.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5"
                    role="listitem"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{token.name}</p>
                      <p className="text-xs text-slate-400">
                        Created {formatDate(token.createdAt)}
                        {token.lastUsedAt && ` · Last used ${formatDate(token.lastUsedAt)}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteTokenMutation.mutate(token.id)}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-danger-50 hover:text-danger-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500 transition-colors duration-150"
                      aria-label={`Delete token ${token.name}`}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* 5. GDPR: Delete account */}
        <Card>
          <h2 className="mb-2 text-base font-semibold text-slate-800">Danger Zone</h2>
          <p className="text-sm text-slate-500 mb-4">
            Requesting account deletion will remove all your data. This cannot be undone.
          </p>
          <Button
            variant="danger"
            onClick={() => setShowDeleteConfirm(true)}
            leftIcon={<Trash2 size={16} />}
          >
            Request Account Deletion
          </Button>
        </Card>
      </main>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Request Account Deletion"
        size="sm"
      >
        <p className="text-sm text-slate-600 mb-1">
          Are you sure you want to request account deletion?
        </p>
        <p className="text-xs text-slate-500 mb-5">
          Your data will be scheduled for deletion within 30 days. Family members will be affected.
          This action is irreversible.
        </p>
        <div className="flex gap-2">
          <Button variant="danger" onClick={() => void handleRequestDeletion()} fullWidth>
            Yes, delete my account
          </Button>
          <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)} fullWidth>
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
}
